import * as React from 'react';
import {Box, Button, Chip, Grid, Menu, MenuItem, Stack, TextField} from "@mui/material";
import Typography from "@mui/material/Typography";
import TimeIntervalsPicker from "./TimeIntervalsPicker";
import DialplanNode, {DialplanNodeIndexContext, DialplanNodePropContext, useDialplanNodeContext} from "./DialplanNode";
import Accordion from "../Accordion";
import Paper from "@mui/material/Paper";
import AddTimeIntervals from "./AddTimeIntervals";
import {useDialplanEditorContext} from './DialplanEditorContext';
import Hangup from "./HangupNode";
import TtsAnnounce from "./TtsAnnounce";
import {DateTimePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import 'moment/locale/it';

function TimeSwitchNode() {
    const {node: {cases}} = useDialplanNodeContext();
    const {isEditMode} = useDialplanEditorContext();
    const [openAccordion, setOpenAccordion] = React.useState(false);
    const lastCaseRef = React.useRef(null);
    
    return (
        <Grid container direction={"column"}>
            <Grid item xs={12}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}>
                    <Typography variant="h6" align="left">
                        ⏲️ Smistamento orario
                    </Typography>
                </div>
            </Grid>
            <Grid item xs={12} sx={{mt: 2}}>
                <Typography variant="body" display={'inline'}>
                    Determina il flusso di risposta in base al giorno e orario della chiamata.
                </Typography>
            </Grid>
            <DialplanNodePropContext prop={"cases"}>
                {isEditMode && <AddTimeSwitchNodeCase lastCaseRef={lastCaseRef} />}
                <Grid item container sx={{mt: 2,}}>
                    {cases.map((it, index) => (
                            <DialplanNodeIndexContext key={index} index={index}>
                                <TimeSwitchNodeCase ref={index === cases.length - 1 ? lastCaseRef : null} />
                            </DialplanNodeIndexContext>
                        )
                    )}
                    {cases.length === 0 && !isEditMode && <Typography>
                        Al momento non sono presenti casistiche basate sull'orario, modifica il piano di risposta per
                        aggiungerle
                    </Typography>}

                </Grid>
            </DialplanNodePropContext>
            <Grid item container sx={{mt: 4}}>
                <Accordion
                    title={"🔄"}
                    description={"Risposta predefinita: In tutti gli altri casi"}
                    expanded={openAccordion}
                    onChange={() => setOpenAccordion(!openAccordion)}>
                    <DialplanNode prop="default"/>
                </Accordion>
            </Grid>
        </Grid>
    )
}


export function AddTimeSwitchNodeCase({ lastCaseRef }) {
    const {node: cases, setNode} = useDialplanNodeContext();
    const addNode = () => {
        setNode([...cases, {intervals: [], node: Hangup.default}]);
        // Scroll alla nuova casistica dopo il render
        setTimeout(() => {
            if (lastCaseRef && lastCaseRef.current) {
                lastCaseRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    }
    return <Grid
        onClick={addNode}
        container
        sx={{
            mt: 2,
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 48,
            backgroundColor: "#e3f2fd",
            borderRadius: 2,
            border: "1px solid #90caf9",
            cursor: "pointer",
            "&:hover": {
                backgroundColor: "#bbdefb"
            },
        }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 1 }}>
            <Typography variant="button" sx={{color: "#1976d2", fontWeight: 500}}>
                + Aggiungi casistica
            </Typography>
            <Typography variant="caption" sx={{color: "#5c6bc0", fontSize: 11}}>
                Definisci quando e come rispondere
            </Typography>
        </Box>
    </Grid>
}

function isCaseDisabled(disabled) {
    if (disabled === undefined) return false;
    if (disabled === true) return true;
    if (disabled?.until) return new Date() < new Date(disabled.until);
    if (disabled?.after) return new Date() >= new Date(disabled.after);
    return false;
}

function formatDisabled(disabled) {
    if (disabled === true) return "Disabilitato";
    if (disabled?.until) return `Disabilitato fino al ${moment(disabled.until).format("DD/MM/YYYY HH:mm")}`;
    if (disabled?.after) {
        if (new Date() >= new Date(disabled.after)) return `Disabilitato (scaduto il ${moment(disabled.after).format("DD/MM/YYYY HH:mm")})`;
        return `Attivo fino al ${moment(disabled.after).format("DD/MM/YYYY HH:mm")}`;
    }
    return "";
}

const TimeSwitchNodeCase = React.forwardRef((props, ref) => {
    const {node, setNode, setNodeChild} = useDialplanNodeContext();
    const {isEditMode} = useDialplanEditorContext();
    const [openAccordion, setOpenAccordion] = React.useState(false);
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const [showDatePicker, setShowDatePicker] = React.useState(false);
    const [datePickerTarget, setDatePickerTarget] = React.useState(null); // 'until' | 'after'
    const [selectedDate, setSelectedDate] = React.useState(moment().add(1, 'day'));

    const disabled = node?.disabled;
    const isDisabled = isCaseDisabled(disabled);
    const hasEnabledUntil = disabled?.after && new Date() < new Date(disabled.after);

    const handleDisableForever = () => {
        setNodeChild("disabled")(true);
        setMenuAnchor(null);
    };

    const handleDisableUntilDate = () => {
        setMenuAnchor(null);
        setSelectedDate(moment().add(1, 'day'));
        setDatePickerTarget('until');
        setShowDatePicker(true);
    };

    const handleEnabledUntilDate = () => {
        setMenuAnchor(null);
        setSelectedDate(moment().add(1, 'day'));
        setDatePickerTarget('after');
        setShowDatePicker(true);
    };

    const handleDateConfirm = () => {
        if (selectedDate && datePickerTarget) {
            setNodeChild("disabled")({ [datePickerTarget]: selectedDate.toISOString() });
        }
        setShowDatePicker(false);
        setDatePickerTarget(null);
    };

    const handleEnable = () => {
        setNodeChild("disabled")(undefined);
    };

    return (
        <Stack sx={{mt: 2, width: "100%"}} ref={ref}>
            <Paper elevation={3} sx={
                isDisabled
                    ? {opacity: 0.6, borderLeft: '4px solid #f44336'}
                    : hasEnabledUntil
                        ? {borderLeft: '4px solid #ed6c02'}
                        : {}
            }>
                <Grid item container sx={{mt: 1, p: 1}}>
                    <Stack flex={1} direction="row" justifyContent={'space-between'} alignItems="center" flexWrap="wrap" gap={1}>
                        {isDisabled && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                    label={formatDisabled(disabled)}
                                    color="error"
                                    size="small"
                                />
                                {isEditMode && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'primary.main',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            '&:hover': { color: 'primary.dark' }
                                        }}
                                        onClick={handleEnable}
                                    >
                                        Riabilita
                                    </Typography>
                                )}
                            </Stack>
                        )}
                        {!isDisabled && hasEnabledUntil && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                    label={formatDisabled(disabled)}
                                    color="warning"
                                    size="small"
                                />
                                {isEditMode && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'primary.main',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            '&:hover': { color: 'primary.dark' }
                                        }}
                                        onClick={handleEnable}
                                    >
                                        Rimuovi scadenza
                                    </Typography>
                                )}
                            </Stack>
                        )}
                        {!isDisabled && !hasEnabledUntil && <Box />}
                        {isEditMode && (
                            <Stack direction="row" spacing={1}>
                                {!isDisabled && !hasEnabledUntil && (
                                    <>
                                        <Button
                                            size="small"
                                            color="warning"
                                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                                        >
                                            Disabilita
                                        </Button>
                                        <Menu
                                            anchorEl={menuAnchor}
                                            open={Boolean(menuAnchor)}
                                            onClose={() => setMenuAnchor(null)}
                                        >
                                            <MenuItem onClick={handleDisableForever}>Disabilita per sempre</MenuItem>
                                            <MenuItem onClick={handleDisableUntilDate}>Disabilita fino a una data...</MenuItem>
                                            <MenuItem onClick={handleEnabledUntilDate}>Attivo fino a una data...</MenuItem>
                                        </Menu>
                                    </>
                                )}
                                <Button color="error" onClick={() => setNode(null)}>
                                    Rimuovi
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                    {isEditMode && showDatePicker && (
                        <Box sx={{ mt: 2, mb: 3, width: '100%' }}>
                            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="it">
                                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems={{xs: 'stretch', sm: 'center'}}>
                                    <Box sx={{flex: 1}}>
                                        <DateTimePicker
                                            label={datePickerTarget === 'until' ? "Riattiva il" : "Attivo fino al"}
                                            ampm={false}
                                            value={selectedDate}
                                            minDateTime={moment()}
                                            onChange={(newValue) => setSelectedDate(newValue)}
                                            renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                                        />
                                    </Box>
                                    <Stack direction="row" spacing={1}>
                                        <Button variant="contained" size="small" onClick={handleDateConfirm}>
                                            Conferma
                                        </Button>
                                        <Button size="small" onClick={() => { setShowDatePicker(false); setDatePickerTarget(null); }}>
                                            Annulla
                                        </Button>
                                    </Stack>
                                </Stack>
                            </LocalizationProvider>
                        </Box>
                    )}
                    <TimeIntervalsPicker intervals={node?.intervals || []}
                                         setIntervals={setNodeChild("intervals")}
                                         isEditMode={isEditMode}
                                         showDayOff={false}/>
                    {isEditMode && <AddTimeIntervals intervals={node.intervals}
                                                     setIntervals={setNodeChild("intervals")}
                                                     daysLabel={"Seleziona i giorni in cui applicare questo intervallo"}
                                                     startTimeLabel={"Seleziona l'orario di inizio"}
                                                     endTimeLabel={"Seleziona l'orario di fine"}
                                                     sx={{mt: 2}}/>
                    }
                </Grid>
                <Grid item container sx={{mt: 4}}>
                    <Accordion title={"🟦"}
                               description={"Configura la risposta per questa casistica oraria"}
                               expanded={openAccordion}
                               onChange={() => setOpenAccordion(!openAccordion)}>
                        <DialplanNode prop="node"/>
                    </Accordion>
                </Grid>
            </Paper>
        </Stack>
    );
});

TimeSwitchNode.title = "Smistamento orario"
TimeSwitchNode.description = "Rispondi in base al giorno e orario della chiamata."
TimeSwitchNode.default = {
    type: "TIME_SWITCH",
    cases: [],
    default: TtsAnnounce.default
}

export default TimeSwitchNode;
